import { Component, Input, Output, EventEmitter, OnInit, Optional } from '@angular/core';
import { Dir } from '@angular/material';

export interface IPageChangeEvent {
  page: number;
  maxPage: number;
  pageSize: number;
  total: number;
  fromRow: number;
  toRow: number;
}
export class TdPagingBarComponent implements OnInit {
private _pageSizes: number[] = [50, 100, 200, 500, 1000];
private _pageSize: number = 50;
private _total: number = 0;
private _page: number = 1;
private _fromRow: number = 1;
private _toRow: number = 1;
private _initialized: boolean = false;
private _pageLinks: number[] = [];
private _pageLinkCount: number = 0;
private _id: string;
private _hitEnd: boolean = false;
private _hitStart: boolean = false;
/**
 * pageSizeAll?: boolean
 * Shows or hides the 'all' menu item in the page size menu. Defaults to 'false'
 */
pageSizeAll: boolean = false;
/**
 * pageSizeAllText?: string
 * Text for the 'all' menu item in the page size menu. Defaults to 'All'
 */
pageSizeAllText: string = 'All';
/**
 * firstLast?: boolean
 * Shows or hides the first and last page buttons of the paging bar. Defaults to 'false'
 */
firstLast: boolean = true;
/**
 * initialPage?: number
 * Sets starting page for the paging bar. Defaults to '1'
 */
initialPage: number = 1;
/**
 * pageLinkCount?: number
 * Amount of page jump to links for the paging bar. Defaults to '0'
 * @param {?} pageLinkCount
 * @return {?}
 */
set pageLinkCount(pageLinkCount: number) {
    this._pageLinkCount = pageLinkCount;
    this._calculatePageLinks();
  }
/**
 * @return {?}
 */
get pageLinkCount(): number {
    return this._pageLinkCount;
  }
/**
 * pageSizes?: number[]
 * Array that populates page size menu. Defaults to [50, 100, 200, 500, 1000]
 * @param {?} pageSizes
 * @return {?}
 */
set pageSizes(pageSizes: number[]) {
    if (!(pageSizes instanceof Array)) {
      throw new Error('[pageSizes] needs to be an number array.');
    }
    this._pageSizes = pageSizes;
    this._pageSize = this._pageSizes[0];
  }
/**
 * @return {?}
 */
get pageSizes(): number[] {
    return this._pageSizes;
  }
/**
 * pageSize?: number
 * Selected page size for the pagination. Defaults to first element of the [pageSizes] array.
 * @param {?} pageSize
 * @return {?}
 */
set pageSize(pageSize: number) {
    if ((this._pageSizes.indexOf(pageSize) > -1 || this.total === pageSize) && this._pageSize !== pageSize) {
      this._pageSize = pageSize;
      this._page = 1;
      if (this._initialized) {
        this._handleOnChange();
      }
    }
  }
/**
 * @return {?}
 */
get pageSize(): number {
    return this._pageSize;
  }
/**
 * total: number
 * Total rows for the pagination.
 * @param {?} total
 * @return {?}
 */
set total(total: number) {
    this._total = total;
    this._calculateRows();
    this._calculatePageLinks();
  }
/**
 * @return {?}
 */
get total(): number {
    return this._total;
  }
/**
 * pageLinks: number[]
 * Returns the pageLinks in an array
 * @return {?}
 */
get pageLinks(): number[] {
    return this._pageLinks;
  }
/**
 * range: string
 * Returns the range of the rows.
 * @return {?}
 */
get range(): string {
    return `${!this._toRow ? 0 : this._fromRow}-${this._toRow}`;
  }
/**
 * page: number
 * Returns the current page.
 * @return {?}
 */
get page(): number {
    return this._page;
  }
/**
 * page: number
 * Returns the max page for the current pageSize and total.
 * @return {?}
 */
get maxPage(): number {
    return Math.ceil(this._total / this._pageSize);
  }
/**
 * id: string
 * Returns the guid id for this paginator
 * @return {?}
 */
get id(): string {
    return this._id;
  }
/**
 * change?: function
 * Method to be executed when page size changes or any button is clicked in the paging bar.
 * Emits an [IPageChangeEvent] implemented object.
 */
onChange: EventEmitter<IPageChangeEvent> = new EventEmitter<IPageChangeEvent>();
/**
 * @return {?}
 */
get isRTL(): boolean {
    if (this._dir) {
      return this._dir.dir === 'rtl';
    }
    return false;
  }
/**
 * @param {?} _dir
 */
constructor(
private _dir: Dir) {
    this._id = this.guid();
  }
/**
 * @return {?}
 */
ngOnInit(): void {
    this._page = this.initialPage;
    this._calculateRows();
    this._calculatePageLinks();
    this._initialized = true;
  }
/**
 * navigateToPage?: function
 * Navigates to a specific valid page. Returns 'true' if page is valid, else 'false'.
 * @param {?} page
 * @return {?}
 */
navigateToPage(page: number): boolean {
    if (page === 1 || (page >= 1 && page <= this.maxPage)) {
      this._page = page;
      this._handleOnChange();
      return true;
    }
    return false;
  }
/**
 * firstPage?: function
 * Navigates to the first page. Returns 'true' if page is valid, else 'false'.
 * @return {?}
 */
firstPage(): boolean {
    return this.navigateToPage(1);
  }
/**
 * prevPage?: function
 * Navigates to the previous page. Returns 'true' if page is valid, else 'false'.
 * @return {?}
 */
prevPage(): boolean {
    return this.navigateToPage(this._page - 1);
  }
/**
 * nextPage?: function
 * Navigates to the next page. Returns 'true' if page is valid, else 'false'.
 * @return {?}
 */
nextPage(): boolean {
    return this.navigateToPage(this._page + 1);
  }
/**
 * lastPage?: function
 * Navigates to the last page. Returns 'true' if page is valid, else 'false'.
 * @return {?}
 */
lastPage(): boolean {
    return this.navigateToPage(this.maxPage);
  }
/**
 * @return {?}
 */
isMinPage(): boolean {
    return this._page <= 1;
  }
/**
 * @return {?}
 */
isMaxPage(): boolean {
    return this._page >= this.maxPage;
  }
/**
 * @return {?}
 */
private _calculateRows(): void {
    let /** @type {?} */ top: number = (this._pageSize * this._page);
    this._fromRow = (this._pageSize * (this._page - 1)) + 1;
    this._toRow = this._total > top ? top : this._total;
  }
/**
 * _calculatePageLinks?: function
 * Calculates the page links that should be shown to the user based on the current state of the paginator
 * @return {?}
 */
private _calculatePageLinks(): void {
    // special case when 2 pageLinks, detect when hit end of pages so can lead in correct direction
    if (this.isMaxPage()) {
      this._hitEnd = true;
      this._hitStart = false;
    }
    // special case when 2 pageLinks, detect when hit start of pages so can lead in correct direction
    if (this.isMinPage()) {
      this._hitEnd = false;
      this._hitStart = true;
    }
    // If the pageLinkCount goes above max possible pages based on perpage setting then reset it to maxPage
    let /** @type {?} */ actualPageLinkCount: number = this.pageLinkCount;
    if (this.pageLinkCount > this.maxPage) {
      actualPageLinkCount = this.maxPage;
    }
    // reset the pageLinks array
    this._pageLinks = [];
    // fill in the array with the pageLinks based on the current selected page
    let /** @type {?} */ middlePageLinks: number = Math.floor(actualPageLinkCount / 2);
    for (let /** @type {?} */ x: number = 0; x < actualPageLinkCount; x++) {
      // don't go past the maxPage in the pageLinks
      // have to handle even and odd pageLinkCounts differently so can still lead to the next numbers
      if ((actualPageLinkCount % 2 === 0 && (this.page + middlePageLinks > this.maxPage)) ||
          (actualPageLinkCount % 2 !== 0 && (this.page + middlePageLinks >= this.maxPage))) {
        this._pageLinks[x] = this.maxPage - (actualPageLinkCount - (x + 1));
      // if the selected page is after the middle then set that page as middle and get the correct balance on left and right
      // special handling when there are only 2 pageLinks to just drop to next if block so can lead to next numbers when moving to right
      // when moving to the left then go into this block
      } else if ((actualPageLinkCount > 2 || actualPageLinkCount <= 2 && this._hitEnd) && (this.page - middlePageLinks) > 0) {
        this._pageLinks[x] = (this.page - middlePageLinks) + x;
      // if the selected page is before the middle then set the pages based on the x index leading up to and after selected page
      } else if ((this.page - middlePageLinks) <= 0) {
        this._pageLinks[x] = x + 1;
      // other wise just set the array in order starting from the selected page
      } else {
        this._pageLinks[x] = this.page + x;
      }
    }
  }
/**
 * @return {?}
 */
private _handleOnChange(): void {
    this._calculateRows();
    this._calculatePageLinks();
    let /** @type {?} */ event: IPageChangeEvent = {
      page: this._page,
      maxPage: this.maxPage,
      pageSize: this._pageSize,
      total: this._total,
      fromRow: this._fromRow,
      toRow: this._toRow,
    };
    this.onChange.emit(event);
  }
/**
 * guid?: function
 * Returns RFC4122 random ("version 4") GUIDs
 * @return {?}
 */
private guid(): string {
    return this.s4() + this.s4() + '-' + this.s4() + '-' + this.s4() + '-' + this.s4() + '-' + this.s4() + this.s4() + this.s4();
  }
/**
 * @return {?}
 */
private s4(): string {
    return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'td-paging-bar',
  templateUrl: './paging-bar.component.html',
  styleUrls: ['./paging-bar.component.css' ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Dir, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'pageSizeAll': [{ type: Input, args: ['pageSizeAll', ] },],
'pageSizeAllText': [{ type: Input, args: ['pageSizeAllText', ] },],
'firstLast': [{ type: Input, args: ['firstLast', ] },],
'initialPage': [{ type: Input, args: ['initialPage', ] },],
'pageLinkCount': [{ type: Input, args: ['pageLinkCount', ] },],
'pageSizes': [{ type: Input, args: ['pageSizes', ] },],
'pageSize': [{ type: Input, args: ['pageSize', ] },],
'total': [{ type: Input, args: ['total', ] },],
'onChange': [{ type: Output, args: ['change', ] },],
};
}

function TdPagingBarComponent_tsickle_Closure_declarations() {
/** @type {?} */
TdPagingBarComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TdPagingBarComponent.ctorParameters;
/** @type {?} */
TdPagingBarComponent.propDecorators;
/** @type {?} */
TdPagingBarComponent.prototype._pageSizes;
/** @type {?} */
TdPagingBarComponent.prototype._pageSize;
/** @type {?} */
TdPagingBarComponent.prototype._total;
/** @type {?} */
TdPagingBarComponent.prototype._page;
/** @type {?} */
TdPagingBarComponent.prototype._fromRow;
/** @type {?} */
TdPagingBarComponent.prototype._toRow;
/** @type {?} */
TdPagingBarComponent.prototype._initialized;
/** @type {?} */
TdPagingBarComponent.prototype._pageLinks;
/** @type {?} */
TdPagingBarComponent.prototype._pageLinkCount;
/** @type {?} */
TdPagingBarComponent.prototype._id;
/** @type {?} */
TdPagingBarComponent.prototype._hitEnd;
/** @type {?} */
TdPagingBarComponent.prototype._hitStart;
/**
 * pageSizeAll?: boolean
 * Shows or hides the 'all' menu item in the page size menu. Defaults to 'false'
 * @type {?}
 */
TdPagingBarComponent.prototype.pageSizeAll;
/**
 * pageSizeAllText?: string
 * Text for the 'all' menu item in the page size menu. Defaults to 'All'
 * @type {?}
 */
TdPagingBarComponent.prototype.pageSizeAllText;
/**
 * firstLast?: boolean
 * Shows or hides the first and last page buttons of the paging bar. Defaults to 'false'
 * @type {?}
 */
TdPagingBarComponent.prototype.firstLast;
/**
 * initialPage?: number
 * Sets starting page for the paging bar. Defaults to '1'
 * @type {?}
 */
TdPagingBarComponent.prototype.initialPage;
/**
 * change?: function
 * Method to be executed when page size changes or any button is clicked in the paging bar.
 * Emits an [IPageChangeEvent] implemented object.
 * @type {?}
 */
TdPagingBarComponent.prototype.onChange;
/** @type {?} */
TdPagingBarComponent.prototype._dir;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
